<script setup lang="ts">
import { Address } from '~/lib/services/store/order/order.dto'
import { getLatestAddresses } from '~/utils/restaurantUtils'
import { aliasCodeToTranslation } from '~/utils/favoriteUtils'

const authStore = useAuthStore()
const restaurantStore = useRestaurantStore()
const countryCode = useCountry()
const { selectAddress } = useAddress(countryCode)
const { trackSelectAddress } = useGTM()
const { t } = useLocale()

const latestAddresses = authStore.userMcId
  ? getLatestAddresses(authStore.userMcId, countryCode)
  : []

const selectedAddress = ref<Address>()

const addressStr = (addrs: Address) => {
  const { address, number } = addrs
  return `${address}${number ? ' ' + number : ''}`
}

const cityStr = (addrs: Address) => {
  const { city, neighborhood } = addrs
  const groups = [neighborhood || '', city].filter(g => g)
  return groups.join(', ')
}

const onSelectAddress = async (addrs: Address) => {
  if (!addrs.latitude || !addrs.longitude) {
    restaurantStore.closeModal()
    return
  }
  await selectAddress(addrs)
}

const setAddress = (addrs: Address) => {
  selectedAddress.value = addrs
}

watch(selectedAddress, async newAddrs => {
  if (newAddrs) {
    trackSelectAddress()
    await onSelectAddress(newAddrs)
    selectedAddress.value = undefined
  }
})
</script>

<template>
  <div class="mt-6 flex flex-col">
    <template v-for="(address, i) in latestAddresses" :key="`latest_address_${i}`">
      <div class="flex w-full cursor-pointer gap-[37px] bg-white px-4 md:px-14">
        <img
          class="hidden md:block"
          src="/images/icons/radioButton.svg"
          alt="radio button"
          @click="setAddress(address)"
        />

        <div class="flex w-full items-start gap-4 py-4" @click="setAddress(address)">
          <!-- Favorite img or previous used -->
          <img
            v-if="address.favoriteId"
            class="h-auto w-4"
            src="/images/icons/favoriteFilled.svg"
            alt="favorite icon"
          />
          <img v-else class="h-auto w-4" src="/images/icons/reorder.svg" alt="reorder icon" />

          <div>
            <!-- Alias if favorite -->
            <p
              v-if="address.favoriteId"
              class="truncate text-xs font-bold leading-4 tracking-[-0.15px]"
            >
              {{ aliasCodeToTranslation({ alias: address.alias, t }) }}
            </p>

            <!-- Address detail -->
            <div class="flex w-full flex-col text-2xs">
              <p>{{ addressStr(address) }}</p>
              <p>{{ cityStr(address) }}</p>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
